/**
 * The invite sheet's `Chats` tab body (ISSUE-1097).
 *
 * Two steps in one tab, like the `Pools` tab. First the fan's chats, read
 * straight from messaging-api's `/chat/list-mine` (newest activity first) and
 * filtered by `resolveInviteSourceChatsState`: a pool's own chat and an
 * unanswered message request are left out. Picking one hands over to
 * `InviteSourcePreview` with `source: "chat"`, which runs
 * `/invite/bulk-preview` for that chat. pool-api asks messaging-api whether the
 * caller is really in the chat, so the server re-checks everything this list
 * filters.
 *
 * Like the `Pools` tab, the list keeps its own failure apart from its own
 * empty, so a dropped connection never reads as "you have no chats".
 */
import { useChatListMine } from "@sportsmet/messaging-sdk/private";
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  CHAT_PREVIEW_EMPTY_BODY,
  CHAT_PREVIEW_FAILED_MESSAGE,
  CHAT_PREVIEW_LOADING_LABEL,
  CHATS_EMPTY_TITLE,
  CHATS_FAILED_MESSAGE,
  CHATS_LOADING_LABEL,
  CHATS_SOURCE_HEADING,
  CHATS_SOURCE_NOTE,
  INVITE_BULK_RETRY_LABEL,
} from "./inviteSheetCopy";
import {
  INVITE_CHAT_LIST_LIMIT,
  inviteChatSourceName,
  resolveInviteSourceChatsState,
  type typeInviteSourceChatRow,
} from "./inviteSourceChats";
import { InviteSourcePreview } from "./InviteSourcePreview";

export type typeInviteChatsTabProps = {
  poolId: string;
};

export const InviteChatsTab = ({
  poolId,
}: typeInviteChatsTabProps): ReactNode => {
  const [source, setSource] = useState<typeInviteSourceChatRow | null>(null);

  const chats = useChatListMine({ limit: INVITE_CHAT_LIST_LIMIT });
  const listState = resolveInviteSourceChatsState({
    isLoading: chats.isLoading,
    hasFailed: chats.isError || Boolean(chats.data?.error),
    items: chats.data?.data?.items ?? null,
  });

  if (source !== null) {
    return (
      <div className="flex flex-col gap-3" data-testid="invite-chats-tab">
        <InviteSourcePreview
          poolId={poolId}
          source="chat"
          sourceId={source.chatId}
          sourceName={inviteChatSourceName(source)}
          copy={{
            loadingLabel: CHAT_PREVIEW_LOADING_LABEL,
            failedMessage: CHAT_PREVIEW_FAILED_MESSAGE,
            emptyBody: CHAT_PREVIEW_EMPTY_BODY,
          }}
          onBack={() => {
            setSource(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="invite-chats-tab">
      {listState.phase === "loading" ? (
        <div data-testid="invite-chats-loading">
          <Skeleton shape="control" label={CHATS_LOADING_LABEL} />
        </div>
      ) : null}
      {listState.phase === "failed" ? (
        <div data-testid="invite-chats-failed">
          <InlineBanner
            variant="warning"
            message={CHATS_FAILED_MESSAGE}
            action={{
              label: INVITE_BULK_RETRY_LABEL,
              onAction: () => {
                void chats.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {listState.phase === "empty" ? (
        <div className="flex flex-col gap-1" data-testid="invite-chats-empty">
          <p className="text-body text-ink">{CHATS_EMPTY_TITLE}</p>
          <p className="text-caption text-ink-secondary">{CHATS_SOURCE_NOTE}</p>
        </div>
      ) : null}
      {listState.phase === "ready" ? (
        <>
          <h3 className="text-body-strong text-ink">{CHATS_SOURCE_HEADING}</h3>
          <p className="text-caption text-ink-secondary">{CHATS_SOURCE_NOTE}</p>
          <ul className="flex flex-col gap-2">
            {listState.rows.map((row) => (
              <li key={row.chatId}>
                <Button
                  fullWidth
                  variant="secondary"
                  title={row.title}
                  data-testid={`invite-source-chat-${row.chatId}`}
                  onClick={() => {
                    setSource(row);
                  }}
                >
                  {/* A chat title has no length cap, and a private chat's
                      title is the other fan's display name. The kit Button
                      never wraps, so the label truncates with an ellipsis
                      instead of clipping both ends and side-scrolling the
                      sheet at 320px. The confirm headline wraps the full
                      name. */}
                  <span className="min-w-0 truncate">{row.title}</span>
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
};
